/**
 * Replace local draft image references before pulse HTML is persisted.
 *
 * A null replacement removes the image (for an upload that failed or an
 * attachment that was removed). URLs not owned by the draft are untouched.
 */
export function rewriteDraftAttachmentImages(
  html: string,
  replacements: ReadonlyMap<string, string | null>,
): string {
  if (!html || replacements.size === 0) return html;

  const container = document.createElement('div');
  container.innerHTML = html;
  let changed = false;

  container.querySelectorAll('img').forEach((image) => {
    const source = image.getAttribute('src');
    if (!source || !replacements.has(source)) return;

    const replacement = replacements.get(source);
    if (replacement) {
      image.setAttribute('src', replacement);
    } else {
      image.remove();
    }
    changed = true;
  });

  return changed ? container.innerHTML : html;
}

export function dropboxImageUrl(sharedLink: string): string {
  try {
    const url = new URL(sharedLink);
    url.searchParams.delete('dl');
    url.searchParams.set('raw', '1');
    return url.toString();
  } catch {
    return sharedLink
      .replace(/([?&])dl=[^&]*/i, '$1raw=1')
      .replace(/[?&]$/, '');
  }
}

export interface DraftAttachmentLike {
  file: { name: string };
  url?: string;
}

export interface DraftUploadResult<TAttachment extends DraftAttachmentLike, TUploaded> {
  uploadedAttachments: Array<{ draft: TAttachment; uploaded: TUploaded }>;
  failedAttachments: TAttachment[];
}

interface PersistPulseDraftOptions<
  TPulse extends Record<string, unknown>,
  TAttachment extends DraftAttachmentLike,
  TUploaded extends { sharedLink?: string | null },
> {
  pulseData: Record<string, unknown> & { htmlContent?: string | null };
  files: TAttachment[];
  createPulse: (payload: Record<string, unknown>) => Promise<TPulse>;
  uploadAttachments: (
    files: TAttachment[],
    pulseId: number,
  ) => Promise<DraftUploadResult<TAttachment, TUploaded>>;
  patchHtml: (pulseId: number, htmlContent: string | null) => Promise<void>;
}

export interface PersistPulseDraftResult<TPulse> {
  pulse: TPulse;
  warnings: string[];
}

/**
 * The post-create portion deliberately never rejects. Once createPulse
 * succeeds, surfacing an overall "create failed" action would invite the user
 * to submit again and create a duplicate pulse. Attachment/inline failures are
 * returned as explicit warnings instead.
 */
export async function persistPulseDraft<
  TPulse extends Record<string, unknown> & { id: number },
  TAttachment extends DraftAttachmentLike,
  TUploaded extends { sharedLink?: string | null },
>({
  pulseData,
  files,
  createPulse,
  uploadAttachments,
  patchHtml,
}: PersistPulseDraftOptions<TPulse, TAttachment, TUploaded>): Promise<PersistPulseDraftResult<TPulse>> {
  const draftImageUrls = new Map<string, string | null>();
  files.forEach(file => {
    if (file.url) draftImageUrls.set(file.url, null);
  });

  const htmlWithoutDraftImages = pulseData.htmlContent
    ? rewriteDraftAttachmentImages(pulseData.htmlContent, draftImageUrls)
    : pulseData.htmlContent ?? null;
  const pulse = await createPulse({
    ...pulseData,
    htmlContent: htmlWithoutDraftImages,
    attachments: [],
  });
  const warnings: string[] = [];
  let persistedHtml = htmlWithoutDraftImages;

  if (files.length > 0) {
    let uploadResult: DraftUploadResult<TAttachment, TUploaded>;
    try {
      uploadResult = await uploadAttachments(files, pulse.id);
    } catch {
      uploadResult = {
        uploadedAttachments: [],
        failedAttachments: files,
      };
    }
    uploadResult.failedAttachments.forEach(file => {
      warnings.push(`${file.file.name} could not be uploaded.`);
    });

    const replacements = new Map(draftImageUrls);
    uploadResult.uploadedAttachments.forEach(({ draft, uploaded }) => {
      if (!draft.url) return;
      if (uploaded.sharedLink) {
        replacements.set(draft.url, dropboxImageUrl(uploaded.sharedLink));
      } else {
        warnings.push(
          `${draft.file.name} was attached, but could not be inserted in the description because its durable link is missing.`,
        );
      }
    });

    const durableHtml = pulseData.htmlContent
      ? rewriteDraftAttachmentImages(pulseData.htmlContent, replacements)
      : pulseData.htmlContent ?? null;

    if (durableHtml !== htmlWithoutDraftImages) {
      try {
        await patchHtml(pulse.id, durableHtml);
        persistedHtml = durableHtml;
      } catch {
        warnings.push(
          'The pulse and attachment were saved, but the image could not be inserted in the description.',
        );
      }
    }
  }

  return {
    pulse: { ...pulse, htmlContent: persistedHtml },
    warnings,
  };
}